/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = cssMap({
	card: {
		marginBlockStart: token('space.150'),
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		borderRadius: token('radius.xxlarge'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		backgroundColor: token('elevation.surface.raised'),
		boxShadow: token('elevation.shadow.raised'),
	},
	icon: {
		objectFit: 'contain',
		width: '24px',
		height: '24px',
	},
});

export interface ConnectorRecommendationProps {
	/** Human-readable name of the third-party app being recommended. */
	friendlyName: string;
	/** Internal identifier for the app source (e.g. "google-drive"). */
	appSource: string;
	/**
	 * Outbound OAuth URL passed through to the consumer. The component itself
	 * does not navigate to or otherwise consume this URL — the consumer is
	 * responsible for any side effects in `onConnect`.
	 */
	outboundAuthUrl: string;
	/**
	 * URL of the connector's logo image. Sourced from the connector store
	 * (e.g. `AgentKnowledgeConfiguration.iconUrl`). When absent, no icon is
	 * rendered.
	 */
	iconUrl?: string;
	/** Called when the user clicks the connect call-to-action. */
	onConnect: (args: { appSource: string; friendlyName: string; outboundAuthUrl: string }) => void;
	/**
	 * Whether the user has successfully connected this app. When true, the
	 * Connect button is replaced with a connected indicator (and optionally a
	 * re-run prompt CTA).
	 */
	isConnected: boolean;
	/**
	 * Whether to show the re-run prompt CTA alongside the connected indicator.
	 * Only relevant when `isConnected` is true. False when the user has sent a
	 * new prompt since the card was shown — re-running would interrupt the
	 * current conversation.
	 */
	canRerunPrompt: boolean;
	/** Called when the user clicks the re-run prompt CTA. */
	onRerunPrompt: () => void;
}

/**
 * Presentational card recommending that the user connect a third-party app.
 *
 * This component is intentionally "dumb": it owns no auth, analytics, or gating
 * logic. The consumer (e.g. the chat surface) is responsible for wiring those
 * concerns and for deciding whether to render the card at all. It is rendered
 * in the response-triggered connector recommendation slot in the chat history.
 *
 * Layout matches the response-triggered connector recommendation design:
 * vertical stack of icon → title → description → CTA button (left-aligned).
 * No dismiss affordance is rendered.
 */
export const ConnectorRecommendation = ({
	friendlyName,
	appSource,
	outboundAuthUrl,
	iconUrl,
	onConnect,
	isConnected,
	canRerunPrompt,
	onRerunPrompt,
}: ConnectorRecommendationProps): React.ReactElement => {
	const { formatMessage } = useIntl();

	const handleConnect = useCallback(() => {
		onConnect({ appSource, friendlyName, outboundAuthUrl });
	}, [onConnect, appSource, friendlyName, outboundAuthUrl]);

	// Derive the card state and all state-dependent content in one memo so
	// that (a) the state logic lives in one place, and (b) formatMessage is
	// only called for the active state.
	const { title, description, cta } = useMemo<{
		title: string;
		description: string;
		cta?: React.ReactNode;
	}>(() => {
		const appName = friendlyName;

		if (!isConnected) {
			return {
				title: formatMessage(messages.unconnectedTitle, { appName }),
				description: formatMessage(messages.unconnectedDescription, { appName }),
				cta: (
					<Button
						appearance="default"
						onClick={handleConnect}
						testId="connector-recommendation-connect-button"
					>
						{formatMessage(messages.connectButton)}
					</Button>
				),
			};
		}

		if (canRerunPrompt) {
			return {
				title: formatMessage(messages.connectedTitle, { appName }),
				description: formatMessage(messages.connectedRerunDescription, { appName }),
				cta: (
					<Button
						appearance="default"
						onClick={onRerunPrompt}
						testId="connector-recommendation-rerun-button"
					>
						{formatMessage(messages.rerunPromptButton)}
					</Button>
				),
			};
		}

		return {
			title: formatMessage(messages.connectedTitle, { appName }),
			description: formatMessage(messages.connectedDescription, { appName }),
		};
	}, [isConnected, canRerunPrompt, friendlyName, formatMessage, handleConnect, onRerunPrompt]);

	return (
		<Box xcss={styles.card} testId="connector-recommendation-card">
			<Stack space="space.100" alignInline="start">
				{iconUrl && (
					<Box
						as="img"
						src={iconUrl}
						alt=""
						xcss={styles.icon}
						testId="connector-recommendation-icon"
					/>
				)}
				<Text size="medium" weight="semibold" color="color.text">
					{title}
				</Text>
				<Text size="small" color="color.text.subtle">
					{description}
				</Text>
				{cta}
			</Stack>
		</Box>
	);
};
